import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import SpeakButton from "../components/SpeakButton";
import { toast } from "sonner";
import { Bot, Send, Target, PartyPopper, Users2, Video, ChevronLeft, Trophy, RotateCcw, BookOpen, GraduationCap, Loader2, Calendar as CalendarIcon, Check } from "lucide-react";

export default function Lesson() {
  const { id } = useParams();
  const [lesson, setLesson] = useState(null);

  useEffect(() => {
    api.get(`/lesson/${id}`).then(r => setLesson(r.data)).catch(() => toast.error("Lesson not found"));
  }, [id]);

  if (!lesson) return <div className="p-12 text-center text-slate-500">Loading lesson…</div>;

  return (
    <div data-testid="lesson-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <Link to="/dashboard" data-testid="lesson-back" className="inline-flex items-center text-sm font-bold text-slate-500 hover:text-slate-900 mb-6">
        <ChevronLeft size={16} strokeWidth={3}/> Back to dashboard
      </Link>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Left: Lesson content */}
        <div className="lg:col-span-7 space-y-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 border border-amber-200 mb-4">
              <span className="text-[10px] font-black uppercase tracking-widest text-amber-800">VA SOL {lesson.sol_code}</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-black text-slate-900 tracking-tight">{lesson.title}</h1>
            <p className="text-slate-500 mt-2 text-sm">Grade {lesson.grade} · {lesson.subject_name}</p>
          </div>

          <section className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-sky-600">
                <Target size={14} strokeWidth={3}/> Today's objectives
              </div>
              <SpeakButton testId="speak-objectives" text={`Today's objectives. ${lesson.objectives.map((o,i)=>`Number ${i+1}. ${o}`).join(". ")}`} />
            </div>
            <ul className="space-y-3">
              {lesson.objectives.map((o, i) => (
                <li key={i} data-testid={`objective-${i}`} className="flex items-start gap-3">
                  <span className="mt-1 w-6 h-6 rounded-full bg-sky-100 text-sky-700 font-black text-xs flex items-center justify-center shrink-0">{i+1}</span>
                  <span className="text-slate-700 text-base lg:text-lg flex-1">{o}</span>
                  <SpeakButton testId={`speak-objective-${i}`} text={o} />
                </li>
              ))}
            </ul>
          </section>

          <LessonContent lessonId={id}/>

          <section className="bg-gradient-to-br from-amber-100 via-white to-rose-100 rounded-3xl p-8 border border-amber-200">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-amber-800">
                <PartyPopper size={14} strokeWidth={3}/> Fun engaging activity
              </div>
              <SpeakButton testId="speak-activity" text={`Fun activity: ${lesson.activity}`} />
            </div>
            <p className="text-slate-800 text-lg lg:text-xl font-semibold leading-relaxed">{lesson.activity}</p>
          </section>

          <Quiz lessonId={id} studentName={lesson.subject_name} />
        </div>

        {/* Right: Tutor + Live */}
        <div className="lg:col-span-5 space-y-6">
          <Tabs defaultValue="ai" className="w-full">
            <TabsList data-testid="lesson-tabs" className="grid grid-cols-2 w-full bg-slate-100 rounded-2xl p-1 h-auto">
              <TabsTrigger value="ai" data-testid="tab-ai" className="rounded-xl font-bold py-3"><Bot size={16} strokeWidth={2.5} className="mr-2"/>AI Tutor</TabsTrigger>
              <TabsTrigger value="live" data-testid="tab-live" className="rounded-xl font-bold py-3"><Users2 size={16} strokeWidth={2.5} className="mr-2"/>Live Teacher</TabsTrigger>
            </TabsList>
            <TabsContent value="ai" className="mt-4">
              <TutorChat lessonId={id} />
            </TabsContent>
            <TabsContent value="live" className="mt-4">
              <LiveBooking lessonId={id} subjectKey={lesson.subject_key} subjectName={lesson.subject_name}/>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function TutorChat({ lessonId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get(`/tutor/history/${lessonId}`).then(r => {
      const m = [];
      r.data.forEach(row => {
        m.push({ who: "user", text: row.user_msg });
        m.push({ who: "bot", text: row.reply });
      });
      setMessages(m);
    });
  }, [lessonId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { who: "user", text: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/tutor/chat", { lesson_id: lessonId, message: msg });
      setMessages(prev => [...prev, { who: "bot", text: data.reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI tutor is taking a break");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="tutor-chat" className="bg-white rounded-3xl border border-slate-100 shadow-sm flex flex-col h-[28rem]">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-sky-100 flex items-center justify-center">
          <Bot size={18} className="text-sky-600" strokeWidth={2.5}/>
        </div>
        <div>
          <div className="font-black text-slate-900 leading-tight">AI Tutor</div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">GPT-5.2 · always patient</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-sm text-slate-400 mt-12">Say hi to start! Try: "Can you help me with this lesson?"</div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.who === "user" ? "justify-end" : "justify-start"} items-end gap-2`}>
            {m.who === "bot" && <SpeakButton testId={`speak-bot-${i}`} text={m.text} size={12} className="w-6 h-6 mb-1"/>}
            <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.who === "user" ? "bg-sky-500 text-white rounded-tr-sm" : "bg-slate-100 text-slate-800 rounded-tl-sm"}`}>
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start"><div className="bg-slate-100 px-4 py-2.5 rounded-2xl text-sm text-slate-500">thinking…</div></div>
        )}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-slate-100 flex gap-2">
        <Input data-testid="tutor-input" value={input} onChange={(e)=>setInput(e.target.value)}
          onKeyDown={(e)=>e.key === "Enter" && send()}
          placeholder="Ask anything about this lesson…" className="rounded-full border-slate-200" />
        <Button data-testid="tutor-send" onClick={send} disabled={sending} className="rounded-full bg-sky-500 hover:bg-sky-600">
          <Send size={16} strokeWidth={2.5}/>
        </Button>
      </div>
    </div>
  );
}

function LessonContent({ lessonId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const fetchContent = async () => {
    setLoading(true); setErr(false);
    try {
      const { data } = await api.get(`/lesson/${lessonId}/content`);
      setData(data);
    } catch (e) {
      setErr(true);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchContent(); }, [lessonId]);

  if (loading) {
    return (
      <section className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-emerald-600 mb-4">
          <BookOpen size={14} strokeWidth={3}/> Lesson
        </div>
        <div className="flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin"/> Preparing your lesson…</div>
      </section>
    );
  }
  if (err) {
    return (
      <section className="bg-white rounded-3xl p-8 border border-rose-200 shadow-sm">
        <div className="text-sm text-rose-700">Could not load the lesson. <button onClick={fetchContent} className="underline font-bold">Try again</button></div>
      </section>
    );
  }
  if (!data) return null;

  const fullText = `Lesson. ${data.intro || ""} ${data.sections.map(s => {
    let part = `${s.heading}. ${s.body}`;
    if (s.prompt) part += ` Now your turn. ${s.prompt}`;
    return part;
  }).join(" ")} ${data.closing || ""}`;

  return (
    <section data-testid="lesson-content" className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-emerald-600">
          <GraduationCap size={14} strokeWidth={3}/> Lesson
        </div>
        <SpeakButton testId="speak-lesson-all" text={fullText} className="bg-emerald-100 hover:bg-emerald-200 text-emerald-700"/>
      </div>
      {data.intro && (
        <div className="flex items-start gap-3 mb-5">
          <p className="text-slate-700 text-base lg:text-lg leading-relaxed flex-1">{data.intro}</p>
          <SpeakButton testId="speak-lesson-intro" text={data.intro}/>
        </div>
      )}
      <div className="space-y-6">
        {data.sections.map((s, i) => (
          <div key={i} data-testid={`lesson-section-${i}`}>
            <div className="border-l-4 border-emerald-200 pl-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-black text-slate-900 text-lg flex-1">{s.heading}</h3>
                <SpeakButton testId={`speak-lesson-${i}`} text={`${s.heading}. ${s.body}`}/>
              </div>
              <p className="text-slate-700 mt-1 leading-relaxed">{s.body}</p>
            </div>
            {s.prompt && <InteractivePrompt prompt={s.prompt} hint={s.hint} type={s.prompt_type} index={i}/>}
          </div>
        ))}
      </div>
      {data.closing && (
        <div className="flex items-start gap-3 mt-6 pt-5 border-t border-slate-100">
          <p className="text-slate-700 text-base lg:text-lg font-semibold flex-1">{data.closing}</p>
          <SpeakButton testId="speak-lesson-closing" text={data.closing}/>
        </div>
      )}
    </section>
  );
}

function InteractivePrompt({ prompt, hint, type, index }) {
  const [showHint, setShowHint] = useState(false);
  const styles = {
    repeat:     { bg: "bg-amber-50",   border: "border-amber-300",   text: "text-amber-800",   chip: "REPEAT AFTER ME",   icon: "🗣️" },
    read_along: { bg: "bg-sky-50",     border: "border-sky-300",     text: "text-sky-800",     chip: "READ ALONG",         icon: "📖" },
    point_to:   { bg: "bg-rose-50",    border: "border-rose-300",    text: "text-rose-800",    chip: "POINT & SAY",        icon: "👉" },
    try_it:     { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", chip: "NOW YOU TRY",        icon: "✨" },
    answer:     { bg: "bg-violet-50",  border: "border-violet-300",  text: "text-violet-800",  chip: "TELL ME",            icon: "💬" },
  };
  const s = styles[type] || styles.try_it;

  return (
    <div data-testid={`interactive-prompt-${index}`} className={`mt-3 ml-4 ${s.bg} ${s.border} border-2 rounded-2xl p-5`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] ${s.text}`}>
          <span className="text-base leading-none">{s.icon}</span> {s.chip}
        </div>
        <SpeakButton testId={`speak-prompt-${index}`} text={`Now your turn. ${prompt}`} className={`${s.bg} hover:opacity-80 ${s.text}`}/>
      </div>
      <p className={`font-bold text-lg ${s.text} leading-relaxed`}>{prompt}</p>
      {hint && (
        <div className="mt-3">
          <button onClick={()=>setShowHint(!showHint)} data-testid={`hint-toggle-${index}`}
                  className={`text-xs font-bold underline ${s.text} opacity-70 hover:opacity-100`}>
            {showHint ? "Hide hint" : "Need a hint?"}
          </button>
          {showHint && (
            <div className={`mt-2 flex items-start gap-2 text-sm italic ${s.text}`}>
              <span className="flex-1">💡 {hint}</span>
              <SpeakButton testId={`speak-hint-${index}`} text={hint} size={11} className="w-5 h-5 shrink-0"/>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LiveBooking({ subjectKey, subjectName }) {
  const [data, setData] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [activeDay, setActiveDay] = useState(0);
  const [confirming, setConfirming] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  const load = async () => {
    setConfirmed(null);
    try {
      const { data } = await api.get(`/availability?week_offset=${weekOffset}`);
      setData(data);
    } catch (e) {
      toast.error("Could not load availability");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekOffset]);

  const book = async (slot) => {
    setConfirming(slot.slot_key);
    try {
      const { data } = await api.post("/availability/book", { slot_key: slot.slot_key, subject_key: subjectKey });
      setConfirmed(data);
      toast.success("Live session booked!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setConfirming(null); }
  };

  if (!data) return <div className="bg-white rounded-3xl p-6 border border-slate-100 text-slate-500 text-sm">Loading availability…</div>;

  const day = data.days[activeDay];

  return (
    <div data-testid="live-booking" className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl bg-rose-100 flex items-center justify-center">
          <Users2 size={18} className="text-rose-600" strokeWidth={2.5}/>
        </div>
        <div>
          <div className="font-black text-slate-900 leading-tight">Live Certified Teacher</div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">M–F 7am–7pm ET · 60 min sessions</div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <button data-testid="prev-week" onClick={()=>setWeekOffset(Math.max(0, weekOffset-1))} disabled={weekOffset === 0}
                className="text-xs font-bold text-slate-500 hover:text-slate-900 disabled:opacity-30">← Previous</button>
        <div className="text-xs font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
          <CalendarIcon size={12} strokeWidth={3}/> Week of {data.week_start}
        </div>
        <button data-testid="next-week" onClick={()=>setWeekOffset(weekOffset+1)}
                className="text-xs font-bold text-slate-500 hover:text-slate-900">Next →</button>
      </div>

      <div className="grid grid-cols-5 gap-1 mb-4">
        {data.days.map((d, i) => (
          <button key={d.day} data-testid={`day-tab-${i}`} onClick={()=>setActiveDay(i)}
                  className={`px-2 py-2 rounded-xl text-xs font-bold transition ${activeDay === i ? "bg-rose-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
            <div className="text-[10px] opacity-80">{d.day.slice(0,3)}</div>
            <div className="text-sm">{d.date.slice(8)}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
        {day.slots.map((slot) => {
          const isBooking = confirming === slot.slot_key;
          const isBooked = slot.booked;
          return (
            <button key={slot.slot_key} type="button" data-testid={`slot-${slot.slot_key}`}
                    onClick={() => !isBooked && book(slot)} disabled={isBooked || isBooking}
                    className={`px-3 py-3 rounded-2xl text-sm font-bold transition border-2 ${
                      isBooked
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700 cursor-default"
                        : "bg-white border-slate-200 hover:border-rose-400 hover:bg-rose-50 text-slate-700"
                    }`}>
              {isBooking ? <Loader2 size={14} className="animate-spin mx-auto"/>
                : isBooked ? <span className="flex items-center justify-center gap-1"><Check size={14} strokeWidth={3}/> Booked</span>
                : slot.label}
            </button>
          );
        })}
      </div>

      {confirmed && (
        <div data-testid="booking-confirmation" className="mt-4 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-sm">
          <div className="font-black text-emerald-800 mb-1">Confirmed!</div>
          <div className="text-emerald-700">{confirmed.teacher_name} · {confirmed.iso_start.replace('T',' ').slice(0,16)}</div>
          <div className="mt-2 flex flex-wrap gap-2">
            <a href={confirmed.meet_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-emerald-200 font-bold text-xs text-emerald-800">
              <Video size={12} strokeWidth={3}/> Google Meet
            </a>
            <a href={confirmed.zoom_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-emerald-200 font-bold text-xs text-emerald-800">
              <Video size={12} strokeWidth={3}/> Zoom
            </a>
          </div>
          <div className="text-xs text-emerald-700 mt-2">{subjectName} · with Ms. Haddis · invite sent to your email</div>
        </div>
      )}
    </div>
  );
}

function Quiz({ lessonId }) {
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get(`/quiz/${lessonId}`);
      setQuestions(data.questions);
      setAnswers(Array(data.questions.length).fill(-1));
    } catch (e) {
      toast.error("Could not generate quiz right now");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (answers.includes(-1)) { toast.error("Pick an answer for each question"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/quiz/submit", { lesson_id: lessonId, answers, questions });
      setResult(data);
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="quiz-section" className="bg-slate-900 text-white rounded-3xl p-8">
      <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-amber-300 mb-3">
        <Trophy size={14} strokeWidth={3}/> End-of-lesson quiz
      </div>
      {!questions && !result && (
        <>
          <p className="text-slate-200 mb-5 text-lg">Ready to show what you've learned? Take the 5-question summary quiz!</p>
          <Button data-testid="quiz-start" onClick={start} disabled={busy}
                  className="rounded-full bg-amber-400 hover:bg-amber-500 text-slate-900 font-black px-8 h-12">
            {busy ? "Generating…" : "Start quiz"}
          </Button>
        </>
      )}

      {questions && !result && (
        <div className="space-y-6">
          {questions.map((q, qi) => (
            <div key={q.id} data-testid={`quiz-question-${qi}`}>
              <div className="flex items-start gap-2 mb-3">
                <div className="font-bold text-lg flex-1">{qi+1}. {q.question}</div>
                <SpeakButton testId={`speak-q-${qi}`} text={`Question ${qi+1}. ${q.question}. ${q.options.map((o,oi)=>`${String.fromCharCode(65+oi)}. ${o}.`).join(" ")}`} className="bg-amber-300 hover:bg-amber-400 text-slate-900 mt-1"/>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {q.options.map((opt, oi) => (
                  <button key={oi} type="button" data-testid={`quiz-q${qi}-opt${oi}`}
                          onClick={()=>setAnswers(a => a.map((v,i)=>i===qi?oi:v))}
                          className={`text-left px-4 py-3 rounded-2xl text-sm font-semibold transition border-2 flex items-center gap-2 ${answers[qi] === oi ? "bg-amber-300 text-slate-900 border-amber-300" : "bg-slate-800 border-slate-700 hover:border-slate-500"}`}>
                    <span className="flex-1">{String.fromCharCode(65+oi)}. {opt}</span>
                    <SpeakButton testId={`speak-q${qi}-opt${oi}`} text={`${String.fromCharCode(65+oi)}. ${opt}`} size={11} className="w-5 h-5 shrink-0"/>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <Button data-testid="quiz-submit" onClick={submit} disabled={busy}
                  className="rounded-full bg-amber-400 hover:bg-amber-500 text-slate-900 font-black px-8 h-12">
            Submit answers
          </Button>
        </div>
      )}

      {result && (
        <div data-testid="quiz-result" className="text-center py-4">
          <div className="text-6xl font-black mb-2">{Math.round(result.score / result.total * 100)}%</div>
          <div className="text-amber-300 font-bold uppercase tracking-widest text-xs mb-3">{result.passed ? "Passed!" : "Try again"}</div>
          <div className="flex items-center justify-center gap-2 mb-6">
            <p className="text-slate-200">{result.feedback}</p>
            <SpeakButton testId="speak-feedback" text={result.feedback} className="bg-amber-300 hover:bg-amber-400 text-slate-900"/>
          </div>
          <Button data-testid="quiz-retake" onClick={start} className="rounded-full bg-white text-slate-900 hover:bg-slate-100 font-black px-6 h-11">
            <RotateCcw size={14} strokeWidth={3} className="mr-2"/> Try a new quiz
          </Button>
        </div>
      )}
    </section>
  );
}
